import Router from 'next/router';

import * as Sentry from '@sentry/nextjs';
import { isbot } from 'isbot';
import { uid } from 'radash';
import { snapshot } from 'valtio';

type SlackCreditCost = { type: CreditUsageType; units: number };

type SlackOptions = {
    notifySlack?: boolean;
    includeAnonymous?: boolean;
    creditCost?: SlackCreditCost;
    details?: string[];
    channel?: SlackChannel.TRACKING | SlackChannel.LOGGING;
};

import { publicConfig } from '@public-config';

import { CreditUsageType, CreditUsageTypeCosts, CreditUsageTypeOptions, SlackChannel, SubscriptionType } from 'models';
import { CompressionHelpers } from 'helpers/CompressionHelpers';
import { SubscriptionHelpers } from 'helpers/SubscriptionHelpers';
import { UserHelpers } from 'helpers/UserHelpers';
import { WorkspaceHelpers } from 'helpers/WorkspaceHelpers';

import { ApiService } from 'services/ApiService';
import { AuthenticationService } from 'services/AuthenticationService';
import { ImpersonationService } from 'services/ImpersonationService';
import { LocalStorageService } from 'services/LocalStorageService';
import { SessionStorageService } from 'services/SessionStorageService';
import { state as currentProject } from 'state/current-project';
import { state as currentTeam } from 'state/current-team';
import { state as currentUser } from 'state/current-user';
import { geoState } from 'state/geo';

const LONG_SESSION_THRESHOLD = 10 * 60 * 1000; // 10 minutes

const InternalTrackingService = {
    started: false,
    disabled: false,

    sessionId: null as string | null,

    queue: [] as any[],

    trackable: () => {
        if (ImpersonationService.isImpersonating()) {
            return false;
        }

        const start = SessionStorageService.get('sessionStart');

        if (start) {
            const now = new Date();
            const elapsed = now.getTime() - new Date(start).getTime();

            return elapsed > 7.77 * 1000; // 7.77 seconds
        }

        return false;
    },

    track: (name: string, data: object = {}, slackOptions: SlackOptions = {}) => {
        try {
            if (
                typeof window === 'undefined' ||
                InternalTrackingService.disabled ||
                InternalTrackingService.isCrawlerOrBot() ||
                ImpersonationService.isImpersonating()
            ) {
                return;
            }

            if (slackOptions.notifySlack) {
                InternalTrackingService.notifySlack(name, data, slackOptions);
            }

            InternalTrackingService.startSession();

            const now = new Date();
            const sessionStart = SessionStorageService.get('sessionStart');
            const event = {
                name,
                data,
                session: {
                    id: InternalTrackingService.sessionId,
                    start: sessionStart,
                    duration: sessionStart ? now.getTime() - new Date(sessionStart).getTime() : 0,
                },
                metadata: InternalTrackingService.metadata(),
                timestamp: now.getTime(),
                date: now.toISOString(),
            };

            if (LocalStorageService.get('internalTrackingServiceLogging') === 'debug') {
                console.log('Tracking Event', event);
            }

            const nextBlobSize = CompressionHelpers.calculateStringSize(
                JSON.stringify([...InternalTrackingService.queue, event]),
            );

            // There's a safe limit of 64 Kb for sendBeacon request.
            // If we cross that limit, just push the current events first
            if (nextBlobSize > 48_000) {
                InternalTrackingService.push();
            }

            InternalTrackingService.queue.push(event);
        } catch (error) {
            console.error('Non-blocking Tracking Error');
            console.error(error);
        }
    },

    link: (identifier: string, label = '') => {
        return {
            'data-trackable-link': 'true',
            'data-trackable-link-label': label ?? '',
            'data-trackable-link-identifier': identifier,
        };
    },

    notifySlack: (name: string, data: object | undefined, slackOptions: SlackOptions) => {
        const user = currentUser.user;
        const { includeAnonymous, creditCost, details, channel } = slackOptions;

        Sentry.addBreadcrumb({
            category: 'slack-messages',
            message: name,
            level: 'info',
            data: data,
        });

        if (!user && !includeAnonymous) {
            return;
        }

        if (user?.internal) {
            return;
        }

        ApiService.post(`${publicConfig.urls.api}/track-event-slack`, {
            eventName: name,
            userIdentifier: user ? user.email : AuthenticationService.getFallbackGoofy(),
            url: typeof window !== 'undefined' ? window.location.href : undefined,
            data,
            details,
            channel,
            metadata: InternalTrackingService.metadata(),
            creditCost: creditCost ? InternalTrackingService.resolveCreditCost(creditCost) : undefined,
        });
    },

    resolveCreditCost: ({ type, units }: SlackCreditCost) => {
        const team = currentTeam.team;
        const creditSubscription = team
            ? SubscriptionHelpers.getSubscriptionData(SubscriptionType.CREDIT, team.subscriptions).subscription
            : undefined;

        return {
            credits: CreditUsageTypeCosts[type] * units,
            label: CreditUsageTypeOptions.find((option) => option.value === type)?.label ?? type,
            freeCredits: team ? SubscriptionHelpers.isFreeSubscription(creditSubscription) : undefined,
        };
    },

    metadata: () => {
        const { pathname, asPath, query } = Router.router
            ? Router
            : {
                  pathname: window.location.pathname,
                  asPath: window.location.pathname,
                  query: window.location.search,
              };

        const referrer = LocalStorageService.get(UserHelpers.localStorageKey.referrer);
        const email = LocalStorageService.get(UserHelpers.localStorageKey.email);
        const userId = LocalStorageService.get(UserHelpers.localStorageKey.userId);

        return {
            title: document.title,
            path: asPath,
            pathname,
            query,
            search: window.location.hash,
            referrer: SessionStorageService.get('tracking-referrer') || document.referrer || null,
            session: InternalTrackingService.sessionId,
            user: currentUser.user?.id || null,
            goofy: currentUser.user?.goofy || null,
            fallbackGoofy: AuthenticationService.getFallbackGoofy(),
            internal: currentUser.user?.internal || false,
            userAgent: navigator.userAgent,
            language: navigator.language,
            languages: navigator.languages,
            team: currentTeam.team?.id || null,
            project: currentProject.project?.id || null,
            workspace: WorkspaceHelpers.getConfig(window.location.host)?.key || null,
            // component: currentComponentState.component?.id || null,
            geo: snapshot(geoState).geo,
            localStorage: {
                referrer,
                email,
                userId,
            },
        };
    },

    startSession: () => {
        if (InternalTrackingService.sessionId) {
            return;
        }

        let id = SessionStorageService.get('sessionId');
        const start = SessionStorageService.get('sessionStart');

        if (!id || !start) {
            id = uid(12);
            SessionStorageService.store('sessionId', id);
            SessionStorageService.store('sessionStart', new Date().toISOString());
        }

        InternalTrackingService.sessionId = id;
    },

    checkLongSession: () => {
        const user = currentUser.user;

        if (!user || user.internal) {
            return;
        }

        if (SessionStorageService.get('longSessionNotified')) {
            return;
        }

        const start = SessionStorageService.get('sessionStart');

        if (!start) {
            return;
        }

        const elapsed = new Date().getTime() - new Date(start).getTime();

        if (elapsed < LONG_SESSION_THRESHOLD) {
            return;
        }

        SessionStorageService.store('longSessionNotified', true);

        InternalTrackingService.track(
            'session.longer-than-10-minutes',
            {},
            { notifySlack: true, channel: SlackChannel.LOGGING },
        );
    },

    start: () => {
        if (InternalTrackingService.started) {
            return;
        }

        window.setInterval(InternalTrackingService.push, 10_000);

        window.setInterval(InternalTrackingService.checkLongSession, 30_000);

        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                InternalTrackingService.push();
            }
        });

        window.addEventListener('beforeunload', () => {
            InternalTrackingService.push();
        });

        window.addEventListener('unload', () => {
            InternalTrackingService.push();
        });

        [
            { event: 'click', name: 'tracker' },
            { event: 'auxclick', name: 'tracker' },
            { event: 'contextmenu', name: 'tracker.contextmenu' },
        ].forEach(({ event, name }) => {
            window.addEventListener(event, (event) => {
                const { target } = event;

                if (!(target instanceof Element)) {
                    return;
                }

                const link = target.closest('[data-trackable-link]');

                if (link) {
                    const url = link.getAttribute('href');
                    const identifier = link.getAttribute('data-trackable-link-identifier');
                    const label = link.getAttribute('data-trackable-link-label');

                    InternalTrackingService.track(name, {
                        url,
                        identifier,
                        label,
                    });
                }
            });
        });

        InternalTrackingService.started = true;
    },

    push: () => {
        if (!InternalTrackingService.trackable()) {
            return;
        }

        if (InternalTrackingService.queue.length) {
            const events = JSON.parse(JSON.stringify(InternalTrackingService.queue));
            InternalTrackingService.queue = [];

            if (!navigator.sendBeacon) {
                console.warn('sendBeacon not supported');

                return;
            }

            const blob = new Blob([JSON.stringify({ events })], {
                type: 'application/json; charset=UTF-8',
            });

            const success = navigator.sendBeacon('/api/secret', blob);

            if (!success) {
                // Possible reasons a sendBeacon call can fail:
                // 1. Spamming the API
                // 2. To big of a payload (we tackle this)
                // 3. The total size of all beacon requests in the queue is too big

                let retryCount = 1;
                let retrySuccess = false;

                const retry = () => {
                    retrySuccess = navigator.sendBeacon('/api/secret', blob);
                    retryCount++;

                    if (retrySuccess) {
                        return;
                    }

                    if (retryCount === 5) {
                        console.error('Retry Tracking Beacon Error');
                        console.log(blob.size, events.length, events);
                    } else {
                        const timeout = [1000, 4000, 10000, 15000][retryCount - 1];

                        setTimeout(retry, timeout);
                    }
                };

                setTimeout(retry, 1000);
            }
        }
    },

    skip: async (action: () => Promise<void>) => {
        InternalTrackingService.disabled = true;
        await action();
        InternalTrackingService.disabled = false;
    },

    isCrawlerOrBot: () => isbot(navigator?.userAgent || ''),
};

export { InternalTrackingService };
